var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import services from 'services';
import EmptyComponent from 'components/EmptyComponent';
import DataTable from 'library/components/DataTable';
import { Chip, monoColumn, Muted, mutedColumn, nameColumn } from 'library/components/DataTable/cells';
import ListScreen, { ListAddButton, ListExportButton, listPager } from 'library/components/ListScreen';
import localeTranslator from 'library/Hocs/LocalTranslator';
import { Supplier as SupplierResource } from 'constants/resources';
import { downloadCsv, todayStamp } from '../csv';
import SupplierDrawer from './SupplierDrawer';
var textOf = function (value) { return (value == null ? '' : String(value)); };
var Suppliers = function (props) {
    var t = props.translate;
    var navigate = useNavigate();
    var location = useLocation();
    var supplierService = services.supplierService, permissionService = services.permissionService;
    var canAdd = permissionService.hasPermissionToAdd(SupplierResource);
    var _a = useState(1), pageNumber = _a[0], setPageNumber = _a[1];
    var _b = useState(''), searchValue = _b[0], setSearchValue = _b[1];
    var _c = useState(function () { var _a; return Boolean((_a = location.state) === null || _a === void 0 ? void 0 : _a.openNewSupplier); }), drawerOpen = _c[0], setDrawerOpen = _c[1];
    var _d = useState(false), exporting = _d[0], setExporting = _d[1];
    var debounceRef = useRef(undefined);
    var _e = supplierService.useSuppliers(pageNumber), data = _e.data, isLoading = _e.isLoading;
    var suppliers = (data === null || data === void 0 ? void 0 : data.suppliers) || [];
    var meta = data === null || data === void 0 ? void 0 : data.meta;
    var page = Number((meta === null || meta === void 0 ? void 0 : meta.page) || pageNumber);
    var pages = Math.max(1, Number((meta === null || meta === void 0 ? void 0 : meta.pages) || 1));
    var total = Number((meta === null || meta === void 0 ? void 0 : meta.count) || suppliers.length);
    var from = Number((meta === null || meta === void 0 ? void 0 : meta.from) || (suppliers.length ? 1 : 0));
    var to = Number((meta === null || meta === void 0 ? void 0 : meta.to) || suppliers.length);
    var visible = useMemo(function () {
        var query = searchValue.trim().toLowerCase();
        if (!query)
            return suppliers;
        return suppliers.filter(function (supplier) {
            return [
                supplier.getId(),
                supplier.getName(),
                supplier.getEmail(),
                supplier.getDisplayedSupplierType()
            ]
                .join(' ')
                .toLowerCase()
                .includes(query);
        });
    }, [suppliers, searchValue]);
    useEffect(function () {
        var state = location.state;
        if (!(state === null || state === void 0 ? void 0 : state.openNewSupplier))
            return;
        setDrawerOpen(true);
        navigate('/suppliers', { replace: true, state: null });
    }, [location.state, navigate]);
    var onSearchValueChange = function (event) {
        var value = event.target.value;
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(function () { return setSearchValue(value); }, 300);
    };
    var exportSuppliers = function () {
        if (exporting)
            return;
        if (!visible.length) {
            message.warning(t('exportEmpty'));
            return;
        }
        setExporting(true);
        try {
            var rows = __spreadArray([
                [t('id'), t('name'), t('email'), t('supplierType')]
            ], visible.map(function (supplier) { return [
                String(supplier.getId()),
                textOf(supplier.getName()),
                textOf(supplier.getEmail()),
                textOf(supplier.getDisplayedSupplierType())
            ]; }), true);
            downloadCsv("suppliers-page-".concat(page, "-").concat(todayStamp(), ".csv"), rows);
        }
        catch (_a) {
            message.error(t('exportFailed'));
        }
        finally {
            setExporting(false);
        }
    };
    var columns = useMemo(function () { return [
        monoColumn({ key: 'id', title: t('id'), width: '72px', variant: 'id', value: function (supplier) { return supplier.getId(); } }),
        nameColumn({ key: 'name', title: t('name'), width: 'minmax(180px, 1.4fr)', value: function (supplier) { return textOf(supplier.getName()); } }),
        mutedColumn({ key: 'email', title: t('email'), width: 'minmax(180px, 1.4fr)', value: function (supplier) { return textOf(supplier.getEmail()); } }),
        {
            key: 'type',
            title: t('supplierType'),
            width: '160px',
            render: function (supplier) { return supplier.getDisplayedSupplierType()
                ? _jsx(Chip, { tone: "is-sky", children: supplier.getDisplayedSupplierType() })
                : _jsx(Muted, {}); }
        }
    ]; }, [t]);
    return (_jsxs(ListScreen, { title: t('title'), count: t('countBadge', { count: total }), subtitle: t('subtitle'), actions: (_jsxs(_Fragment, { children: [_jsx(ListExportButton, { label: t('export'), busyLabel: t('exporting'), busy: exporting, onClick: exportSuppliers }), canAdd && _jsx(ListAddButton, { label: t('common:buttons.newSupplier'), onClick: function () { return setDrawerOpen(true); } })] })), search: { placeholder: t('searchPlaceholder'), onChange: onSearchValueChange }, children: [_jsx(DataTable, { minWidth: "720px", loading: isLoading, rows: visible, rowKey: function (supplier) { return supplier.getId(); }, empty: _jsx(EmptyComponent, { description: t('empty') }), columns: columns, pager: listPager({
                    total: total,
                    page: page,
                    pages: pages,
                    meta: searchValue.trim() ? t('matchesOnPage', { count: visible.length }) : t('showingRange', { from: from, to: to, total: total }),
                    previous: t('previous'),
                    next: t('next'),
                    center: t('pageOf', { page: page, pages: pages }),
                    setPage: setPageNumber
                }) }), _jsx(SupplierDrawer, { visiable: drawerOpen, onCloseDrawer: function () { return setDrawerOpen(false); } })] }));
};
export default localeTranslator(Suppliers, 'suppliers');
